/**
 * unresolvedMentions — which @handles in a message matched NO teammate
 * (2026-09-08, chat-thread experience plan PR-1).
 *
 * The @-mention summon used to fail closed AND silent: "@justinzelaya hello"
 * in a solo thread resolved to nobody (the member doc's `email` was blank, so
 * the handle had nothing to match) and the send went out as if nothing had
 * been asked. The composer now says which handles matched nobody. Pure — no
 * React, no firebase — so the rule is unit-tested on its own.
 */
import { normalizeRosterEntries } from '../threadAddPeople/workspaceRosterModel';
import {
  extractPlainMentionQueries,
  resolveMentionsToUids,
  type MentionRosterEntry,
} from './mentionResolver';
import { mentionsAssistant } from './summonGate';

/**
 * The plain handles in `text` that resolve to nobody on `roster`, minus
 * Prest0n's own handles (the assistant is not a roster member). De-duped,
 * first-seen order.
 */
export function findUnresolvedMentionHandles(
  text: string,
  roster: ReadonlyArray<MentionRosterEntry>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const handle of extractPlainMentionQueries(text)) {
    const key = handle.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (mentionsAssistant(`@${handle}`)) continue;
    if (resolveMentionsToUids(`@${handle}`, roster).length === 0) out.push(handle);
  }
  return out;
}

/**
 * The mention roster from raw member docs — ONE normalizer for every people
 * surface (the picker, the composer, the resolver), so a blank `email` falls
 * back to `profileEmail` here exactly as it does in the add-people list.
 */
export function toMentionRoster(rawMembers: readonly unknown[]): MentionRosterEntry[] {
  return normalizeRosterEntries(rawMembers, 'firestore').map((entry) => ({
    userId: entry.userId,
    ...(entry.displayName ? { displayName: entry.displayName } : {}),
    ...(entry.email ? { email: entry.email } : {}),
  }));
}
